import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MDBBadge, MDBBtn, MDBCardBody, MDBTable, MDBTableHead, MDBTableBody } from 'mdb-react-ui-kit';
import { Toast, Modal, Button } from 'react-bootstrap';
import Filter from './Filter';
import FilterMobileView from './FilterMobileView';
import '../styles/InfluencersList.css';
import InstagramSvg from '../assets/icons/instagram.svg';
import TikTokSvg from '../assets/icons/tiktok-icon.svg';
import PhoneSvg from '../assets/icons/whatsapp-color-icon.svg';
import AddToList from '../assets/icons/add-list.svg';
import editIcon from '../assets/icons/Edit.png';
import deleteIcon from '../assets/icons/Trash_24px.png';
import saveIcon from '../assets/icons/save.svg';
import importIcon from '../assets/icons/Import_24px.png';
import exportIcon from '../assets/icons/Export_24px.png';
import { saveAs } from 'file-saver';
import { useNavigate } from 'react-router-dom';
import { getInfluencersList, updateInfluencerDetails, logUserActivity, deleteInfluencer, importCSVFile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import AddToShortlist from './AddToShortlist';
import Pagination from './Pagination';
import { FaSort } from "react-icons/fa";

export default function InfluencersList({ isMobile }) {
    const [allInfluencers, setAllInfluencers] = useState([]);
    const [filteredInfluencers, setFilteredInfluencers] = useState([]);
    const [selectedInfluencers, setSelectedInfluencers] = useState([]);
    const [selectedShortlist, setSelectedShortlist] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'creatorName', direction: 'ascending' });
    const [isEditing, setIsEditing] = useState(null);
    const [editedData, setEditedData] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showAddShortlistModal, setShowAddShortlistModal] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('green');
    const [influencerToDelete, setInfluencerToDelete] = useState(null);
    const { username, userRole } = useAuth();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(8);
    const totalPages = Math.ceil(filteredInfluencers.length / itemsPerPage);

    // Memoized sorting logic
    const sortedInfluencers = useMemo(() => {
        return [...filteredInfluencers].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];
            const direction = sortConfig.direction === 'ascending' ? 1 : -1;
            return aVal < bVal ? -direction : aVal > bVal ? direction : 0;
        });
    }, [filteredInfluencers, sortConfig]);

    // Data fetching and enrichment
    useEffect(() => {
        const enrichInfluencerData = (influencer) => {
            const niches = influencer.niche.split(',').map(niche => niche.trim());
            const platform = influencer.instagramUsername && influencer.tikTokUsername
                ? 'Instagram & TikTok'
                : influencer.instagramUsername ? 'Instagram' : 'TikTok';

            const enrichedInfluencer = { ...influencer, niches, platform };

            if (influencer.workPlatformName === 'Instagram') {
                enrichedInfluencer.IG_followers = influencer.followers;
                enrichedInfluencer.IG_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.IG_avgLikes = influencer.averageLikes;
                enrichedInfluencer.IG_avgComments = influencer.averageComments;
            } else if (influencer.workPlatformName === 'TikTok') {
                enrichedInfluencer.TT_followers = influencer.followers;
                enrichedInfluencer.TT_engagementRate = influencer.engagementRate * 100;
                enrichedInfluencer.TT_avgLikes = influencer.averageLikes;
                enrichedInfluencer.TT_avgComments = influencer.averageComments;
            }

            return enrichedInfluencer;
        };

        const mergeInfluencerData = (data) => {
            return Object.values(data.reduce((acc, curr) => {
                if (!acc[curr.influencerId]) {
                    acc[curr.influencerId] = curr;
                } else {
                    acc[curr.influencerId] = {
                        ...acc[curr.influencerId],
                        ...curr,
                        IG_followers: acc[curr.influencerId].IG_followers || curr.IG_followers,
                        IG_engagementRate: acc[curr.influencerId].IG_engagementRate || curr.IG_engagementRate,
                        IG_avgLikes: acc[curr.influencerId].IG_avgLikes || curr.IG_avgLikes,
                        IG_avgComments: acc[curr.influencerId].IG_avgComments || curr.IG_avgComments,
                        TT_followers: acc[curr.influencerId].TT_followers || curr.TT_followers,
                        TT_engagementRate: acc[curr.influencerId].TT_engagementRate || curr.TT_engagementRate,
                        TT_avgLikes: acc[curr.influencerId].TT_avgLikes || curr.TT_avgLikes,
                        TT_avgComments: acc[curr.influencerId].TT_avgComments || curr.TT_avgComments
                    };
                }
                return acc;
            }, {}));
        };

        const fetchData = async () => {
            try {
                const { data } = await getInfluencersList();
                const enrichedData = data.map(enrichInfluencerData);
                const mergedData = mergeInfluencerData(enrichedData);

                setAllInfluencers(mergedData);
                setFilteredInfluencers(mergedData);
                setSelectedInfluencers(Array(mergedData.length).fill(false));
            } catch (error) {
                console.error('Error fetching influencers data:', error);
            }
        };

        fetchData();
    }, [toastMessage]);

    // Utility functions
    const formatFollowers = useCallback(count => {
        const formats = [
            { threshold: 1e9, suffix: 'B' },
            { threshold: 1e6, suffix: 'M' },
            { threshold: 1e3, suffix: 'K' }
        ];

        const format = formats.find(f => count >= f.threshold);
        if (!format) return count.toString();

        return (count / format.threshold).toFixed(1).replace(/\.0$/, '') + format.suffix;
    }, []);

    const handleFilter = useCallback(filtered => {
        setFilteredInfluencers(filtered);
        setSelectedInfluencers(Array(filtered.length).fill(false));
        setCurrentPage(1); // ✅ Always go to page 1 after filter
      }, []);
      

      const handleSelectAll = useCallback(event => {
        setSelectedInfluencers(Array(filteredInfluencers.length).fill(event.target.checked));
      }, [filteredInfluencers.length]);
      

      const handleSelect = useCallback(index => {
        setSelectedInfluencers(prev => {
          const updated = [...prev];
          updated[index] = !updated[index];
          return updated;
        });
      }, []);      

    const requestSort = useCallback(key => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending'
        }));
    }, []);

    const handleDelete = async () => {
        try {
            if (influencerToDelete) {
                await deleteInfluencer(influencerToDelete.influencerId);
                await logUserActivity({
                    apiEndpoint: `Delete Influencer with ID ${influencerToDelete.influencerId}`,
                    username: username
                });

                // Update the lists after deletion
                const updatedInfluencers = allInfluencers.filter(
                    influencer => influencer.influencerId !== influencerToDelete.influencerId
                );
                setAllInfluencers(updatedInfluencers);
                setFilteredInfluencers(updatedInfluencers);
            }

            setToastMessage('Influencer deleted successfully!');
            setShowToast(true);
            setShowDeleteModal(false);
            setInfluencerToDelete(null);
        } catch (error) {
            console.error('Error deleting influencer:', error);
            setToastMessage('Error deleting influencer');
            setShowToast(true);
        }
    };

    const handleDeleteClick = (influencer) => {
        setShowDeleteModal(true);
        console.log("influencer", influencer);
        setInfluencerToDelete(influencer);
    };

    const exportToCSV = async (userRole) => {
        const csvRows = [];
        const headers = ['CreatorName', 'InstagramUsername', 'TikTokUsername', 'OtherPlatforms', 'Email', 'Phone', 'TelegramUsername', 'HomeAddress', 'Gender', 'Languages', 'CurrentlyLiving', 'Age', 'Rate', 'Niche', 'IG Followers', 'IG Engagement Rate', 'TT Followers', 'TT Engagement Rate', 'Internal Notes'];
        csvRows.push(headers.join(','));

        // Limit the number of influencers if user role is not Administrator
        const maxExportCount = userRole !== 'Administrator' ? 50 : sortedInfluencers.length;

        sortedInfluencers.slice(0, maxExportCount).forEach((influencer, index) => {
            if (selectedInfluencers[index]) {
                const row = [
                    influencer.creatorName || '',
                    influencer.instagramUsername || '',
                    influencer.tikTokUsername || '',
                    influencer.otherPlatforms || '',
                    influencer.email || '',
                    influencer.phone || '',
                    influencer.telegramUsername || '',
                    influencer.homeAddress || '',
                    influencer.gender || '',
                    influencer.languages || '',
                    influencer.currentlyLiving || '',
                    influencer.age || '',
                    influencer.rate || '',
                    influencer.niche || '',
                    influencer.IG_followers || '0',
                    influencer.IG_engagementRate || '0',
                    influencer.TT_followers || '0',
                    influencer.TT_engagementRate || '0',
                    influencer.internalNotes || ''
                ];
                // Ensure each value is properly escaped and wrapped in quotes to prevent CSV formatting issues
                const formattedRow = row.map(value => `"${value.toString().replace(/"/g, '\\"')}"`).join(',');
                csvRows.push(formattedRow);
            }
        });

        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        saveAs(blob, 'influencer.csv');

        await logUserActivity({
            apiEndpoint: `Export CSV file with ${csvRows.length - 1} influencer records`, // Excluding the header row
            username: username
        });
    };


    const handleImport = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await importCSVFile(formData);

            if (response.status === 200) {
                setToastMessage('Data imported successfully!');

                // Log the import activity
                await logUserActivity({
                    apiEndpoint: 'Import CSV file and update influencer records',
                    username: username
                });

                // Refresh the influencers list after successful import
                const updatedInfluencers = await getInfluencersList();
                setAllInfluencers(updatedInfluencers.data);
                setFilteredInfluencers(updatedInfluencers.data);
            } else {
                throw new Error(`Import failed with status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error importing data:', error);
            setToastMessage(error.message || 'Error importing data');
        } finally {
            setShowToast(true);
        }
    };

    const handleRowClick = influencer => {
        if (isEditing !== influencer.creatorName) {
            console.log("influencer", influencer);
            localStorage.setItem(`selectedInfluencer${influencer.influencerId}`, JSON.stringify(influencer));
            window.open(`/details?id=${influencer.influencerId}`, '_blank');
        }
    };

    const handleEdit = influencer => {
        setIsEditing(influencer.creatorName);
        setEditedData({
            influencerId: influencer.influencerId,
            email: influencer.email,
            phone: influencer.phone,
            rate: influencer.rate,
            favorite: influencer.favorite,
            instagramUsername: influencer.instagramUsername,
            tikTokUsername: influencer.tikTokUsername
        });
    };

    const handleSave = async (influencer) => {
        const updatedFields = {};

        if (influencer.email !== editedData.email) {
            updatedFields.email = editedData.email;
        }
        if (influencer.phone !== editedData.phone) {
            updatedFields.phone = editedData.phone;
        }
        if (influencer.rate !== editedData.rate) {
            updatedFields.rate = editedData.rate;
        }
        if (influencer.favorite !== editedData.favorite) {
            updatedFields.favorite = editedData.favorite;
        }
        if (influencer.instagramUsername !== editedData.instagramUsername) {
            updatedFields.isUsernameUpdated = true;
            updatedFields.instagramUsername = editedData.instagramUsername;
        }
        if (influencer.tikTokUsername !== editedData.tikTokUsername) {
            updatedFields.isUsernameUpdated = true;
            updatedFields.tikTokUsername = editedData.tikTokUsername;
        }
        console.log("updatedFields", updatedFields);
        // Check if there are any changes
        if (Object.keys(updatedFields).length > 0) {
            Object.assign(influencer, updatedFields);

            try {
                console.log("influencer", influencer);
                const response = await updateInfluencerDetails(influencer);
                setIsEditing(null);
                await logUserActivity({
                    apiEndpoint: `Update Influencer named ${influencer.creatorName}'s details`,
                    username: username
                });
                setToastMessage('Details updated successfully!');
                setShowToast(true);

                // Update the filtered and all influencers lists
                setFilteredInfluencers(prev =>
                    prev.map(inf => inf.influencerId === influencer.influencerId ? { ...inf, ...influencer } : inf)
                );
                setAllInfluencers(prev =>
                    prev.map(inf => inf.influencerId === influencer.influencerId ? { ...inf, ...influencer } : inf)
                );
            } catch (error) {
                console.error('Error updating influencer data:', error);
            }
        } else {
            setIsEditing(null);
        }
    };


    const handleChange = e => {
        const { name, value } = e.target;
        setEditedData(prev => ({ ...prev, [name]: value }));
    };

    const toggleFavorite = () => {
        setEditedData(prev => ({ ...prev, favorite: !prev.favorite }));
    };

    const handleAddShortlist = () => {
        setShowAddShortlistModal(true);
        const newShortlist = [];

        sortedInfluencers.forEach((influencer, index) => {
            if (selectedInfluencers[index]) {
                newShortlist.push(influencer);
            }
        });

        setSelectedShortlist(prev => [...prev, ...newShortlist]);
    };

    const handleShorlistModalClose = () => {
        setShowAddShortlistModal(false)
        setSelectedShortlist([])
    }


    const updateNotification = (message, type) => {
        setToastMessage(message);
        setToastType(type)
        setShowToast(true);
    }

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInfluencers = filteredInfluencers.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <div className=''>
            <MDBCardBody>

                {/* ✅ Hide Filter on Mobile */}
                {!isMobile && <Filter influencers={allInfluencers} onFilter={handleFilter} />}
                 {/* ✅ Hide Filter on Mobile */}
                 {isMobile && <FilterMobileView influencers={allInfluencers} onFilter={handleFilter} />}
                
                <div className="card">
                    <div className="card-body" style={{ padding: "5px" }}>
                        <div className="heading d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center py-2">
                            <div className="mb-2 mb-md-0">
                                <h3>
                                    <strong>Discover Creator</strong>{' '}
                                    (<strong>{sortedInfluencers.length}</strong>{' '}
                                    <span className="text-muted">Influencers</span>)
                                </h3>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                                {selectedInfluencers.some(Boolean) && (
                                    <MDBBtn
                                        style={{
                                            borderRadius: '23px',
                                            // background: '#875FFE',
                                            // color: '#FFFFFF',
                                            padding: '8px 24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 600,
                                            // boxShadow: '0px 2px 0px 0px #6f4ad4',
                                            height: '40px',
                                            minWidth: '117px'
                                        }}
                                        color="border"
                                        className="border border-primary"
                                        onClick={handleAddShortlist}
                                    >
                                        <img
                                            src={AddToList}
                                            alt="add-to-list"
                                            className="me-1"
                                            width={16}
                                            height={16}
                                        />
                                        Add To List
                                    </MDBBtn>
                                )}
                                <MDBBtn
                                    style={{
                                        borderRadius: '23px',
                                        background: '#875FFE',
                                        color: '#FFFFFF',
                                        padding: '8px 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 600,
                                        boxShadow: '0px 2px 0px 0px #6f4ad4',
                                        height: '40px',
                                        minWidth: '117px'
                                    }}
                                    onClick={() => exportToCSV(userRole)}
                                >
                                    <span style={{ marginRight: '8px' }}>Export</span>
                                    <img
                                        src={exportIcon}
                                        alt="Export"
                                        style={{ width: '16px', height: '16px' }}
                                    />
                                </MDBBtn>
                                {userRole === 'Administrator' && (
                                    <>
                                        <input
                                            type="file"
                                            accept=".csv"
                                            style={{ display: 'none' }}
                                            id="csvFileInput"
                                            onChange={handleImport}
                                        />
                                        <MDBBtn
                                            style={{
                                                borderRadius: '23px',
                                                background: '#FFFFFF',
                                                color: '#875FFE',
                                                padding: '8px 24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600,
                                                border: '1px solid #875FFE',
                                                boxShadow: '0px 2px 0px 0px #875FFE',
                                                height: '40px',
                                                minWidth: '117px'
                                            }}
                                            onClick={() =>
                                                document.getElementById('csvFileInput').click()
                                            }
                                        >
                                            <span style={{ marginRight: '8px' }}>Import</span>
                                            <img
                                                src={importIcon}
                                                alt="Import"
                                                style={{ width: '16px', height: '16px' }}
                                            />
                                        </MDBBtn>
                                    </>
                                )}
                            </div>
                        </div>


                        <MDBTable responsive>

                            <MDBTableHead>
                                <tr className="table-header">
                                    <th scope="col" className="column-checkbox">
                                        <input
                                            type="checkbox"
                                            onChange={handleSelectAll}
                                            checked={selectedInfluencers.every(Boolean)}
                                            className="custom-checkbox"
                                        />
                                    </th>
                                    <th className="sortable-header" onClick={() => requestSort("creatorName")}>
                                        Creator Name <FaSort />
                                    </th>
                                    <th>Contact Number</th>
                                    <th className="sortable-header" onClick={() => requestSort("followers")}>
                                        Followers <FaSort />
                                    </th>
                                    <th>Platform</th>
                                    <th className="sortable-header" onClick={() => requestSort("rate")}>
                                        Rate <FaSort />
                                    </th>
                                    <th>Action</th>
                                </tr>
                            </MDBTableHead>

                            <MDBTableBody>
                                {currentInfluencers.map((influencer, index) => (
                                    <tr key={index}>
                                        <td>
                                            <input
                                                type="checkbox"
                                                checked={selectedInfluencers[index]}
                                                onChange={() => handleSelect(index)}
                                                className="custom-checkbox"
                                            />
                                        </td>
                                        <td onClick={() => handleRowClick(influencer)} className='cursor-pointer'>
                                            <div className='d-flex align-items-center'>
                                                <img
                                                    src={influencer.profileImg ? influencer.profileImg : 'https://mdbcdn.b-cdn.net/img/Photos/new-templates/bootstrap-profiles/avatar-2.webp'}
                                                    alt=''
                                                    style={{ width: '50px', height: '50px' }}
                                                    className='rounded-circle'
                                                />
                                                <div className='ms-3'>
                                                    <p className='fw-bold mb-1'>{influencer.creatorName}
                                                        {/* {isEditing === influencer.creatorName ? (
                                                                <span onClick={toggleFavorite} style={{ cursor: 'pointer', marginLeft: '8px' }}>
                                                                    {editedData.favorite ? '❤️' : '🤍'}
                                                                </span>
                                                            ) : (
                                                                <span className="ms-2">
                                                                    {influencer.favorite ? '❤️' : '🤍'}
                                                                </span>
                                                            )} */}
                                                    </p>
                                                    {isEditing === influencer.creatorName ? (
                                                        <input type="text" name="email" value={editedData.email} className='border bg-transparent shadow-1-soft' onChange={handleChange} />
                                                    ) : (
                                                        <p className='text-muted mb-0 mt-0'><i>{influencer.email}</i></p>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            {(influencer.phone || isEditing) && (
                                                <div className="d-flex align-items-center me-5 mb-3">
                                                    <div className="me-1 content-icon">
                                                        <a href={`https://web.whatsapp.com/send?phone=${influencer.phone}`} target="_blank" rel="noopener noreferrer"><img src={PhoneSvg} alt="Instagram" className='mx-1' style={{ width: '20px', height: '20px' }} /></a>
                                                    </div>
                                                    {isEditing === influencer.creatorName ? (
                                                        <div><input type="text" className="form-control" name="phone" placeholder="Phone number" value={editedData.phone} onChange={handleChange} /></div>
                                                    ) : (
                                                        <a href={`https://web.whatsapp.com/send?phone=${influencer.phone}`} target="_blank" rel="noopener noreferrer">{influencer.phone}</a>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {/* {formatFollowers(influencer?.TT_followers || 0)} */}
                                            <div><img src={InstagramSvg} alt="Instagram" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} /> {formatFollowers(influencer?.IG_followers || 0)}</div>
                                            <div><img src={TikTokSvg} alt="TikTok" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} /> {formatFollowers(influencer?.TT_followers || 0)}</div>
                                        </td>
                                        <td>
                                            {isEditing === influencer.creatorName ? (
                                                <>
                                                    <div className='d-flex align-items-center'>
                                                        <img src={InstagramSvg} alt="Instagram" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} />
                                                        <input
                                                            type="text"
                                                            name="instagramUsername"
                                                            value={editedData.instagramUsername}
                                                            className="border bg-transparent shadow-1-soft"
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className='d-flex align-items-center'>
                                                        <img src={TikTokSvg} alt="Instagram" className='my-1' style={{ width: '20px', height: '20px', marginRight: '2px' }} />
                                                        <input
                                                            type="text"
                                                            name="tikTokUsername"
                                                            value={editedData.tikTokUsername}
                                                            className="border bg-transparent shadow-1-soft"
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    {influencer.platform === 'Instagram' && (
                                                        <a
                                                            href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <img
                                                                src={InstagramSvg}
                                                                alt="Instagram"
                                                                style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                            />
                                                            {influencer.instagramUsername}
                                                        </a>
                                                    )}
                                                    {influencer.platform === 'TikTok' && (
                                                        <a
                                                            href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <img
                                                                src={TikTokSvg}
                                                                alt="TikTok"
                                                                style={{ width: '20px', height: '20px', marginRight: '5px' }}
                                                            />
                                                            {influencer.tikTokUsername}
                                                        </a>
                                                    )}
                                                    {influencer.platform === 'Instagram & TikTok' && (
                                                        <>
                                                            <a
                                                                href={`https://instagram.com/${influencer.instagramUsername.replace('@', '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src={InstagramSvg}
                                                                    className="mx-1 my-1"
                                                                    alt="Instagram"
                                                                    style={{ width: '20px', height: '20px' }}
                                                                />
                                                                {influencer.instagramUsername}
                                                            </a>
                                                            <br />
                                                            <a
                                                                href={`https://tiktok.com/${influencer.tikTokUsername.startsWith('@') ? influencer.tikTokUsername : `@${influencer.tikTokUsername}`}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                <img
                                                                    src={TikTokSvg}
                                                                    alt="TikTok"
                                                                    className="mx-1 my-1"
                                                                    style={{ width: '20px', height: '20px' }}
                                                                />
                                                                {influencer.tikTokUsername}
                                                            </a>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {isEditing === influencer.creatorName ? (
                                                <input type="text" name="rate" value={editedData.rate} className='border bg-transparent shadow-1-soft' onChange={handleChange} />
                                            ) : (
                                                <MDBBadge className="custom-badge">
                                                    {influencer.rate}
                                                </MDBBadge>
                                            )}
                                        </td>
                                        <td>
                                            <MDBBtn
                                                color="link"
                                                className="p-1"
                                                onClick={() =>
                                                    isEditing === influencer.creatorName ? handleSave(influencer) : handleEdit(influencer)
                                                }
                                            >
                                                {isEditing === influencer.creatorName ? (
                                                    <img
                                                        src={saveIcon}
                                                        alt="TikTok"
                                                        className="mx-1 my-1"
                                                        style={{ width: '20px', height: '20px' }}
                                                    />
                                                ) : (
                                                    <img
                                                        src={editIcon}
                                                        alt="TikTok"
                                                        className="mx-1 my-1"
                                                        style={{ width: '20px', height: '20px' }}
                                                    />
                                                )}
                                            </MDBBtn>

                                            {userRole === "Administrator" && (
                                                <MDBBtn color="link" className="p-1 text-danger" onClick={() => handleDeleteClick(influencer)}>
                                                    <img
                                                        src={deleteIcon}
                                                        alt="TikTok"
                                                        className="mx-1 my-1"
                                                        style={{ width: '20px', height: '20px' }}
                                                    />
                                                </MDBBtn>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </MDBTableBody>
                        </MDBTable>
                        {/* Desktop layout */}
                        {!isMobile && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="custom-dropdown">
                                    <p className="dropdown-label">Showing:</p>
                                    <select className="custom-select" onChange={handleItemsPerPageChange} value={itemsPerPage}>
                                        <option value={8}>8 Per page</option>
                                        <option value={20}>20 Per page</option>
                                        <option value={50}>50 Per page</option>
                                        <option value={80}>80 Per page</option>
                                        <option value={100}>100 Per page</option>
                                    </select>
                                </div>

                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                />

                                <div className="custom-dropdown">
                                    <p className="dropdown-label">Go To Page:</p>
                                    <select
                                        className="custom-select-page"
                                        onChange={(e) => handlePageChange(Number(e.target.value))}
                                        value={currentPage}
                                    >
                                        {[...Array(totalPages)].map((_, index) => (
                                            <option key={index + 1} value={index + 1}>
                                                {index + 1}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Mobile layout */}
                        {isMobile && (
                            <div className="pagination-controls mt-3">
                                <div className="dropdown-row">
                                    <div className="dropdown-group">
                                        <select className="custom-select-mobile" onChange={handleItemsPerPageChange} value={itemsPerPage}>
                                            <option value={8}>8</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={80}>80</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <p className="dropdown-label-mobile">Showing</p>
                                    </div>

                                    <div className="dropdown-group">
                                        <select
                                            className="custom-select-mobile"
                                            onChange={(e) => handlePageChange(Number(e.target.value))}
                                            value={currentPage}
                                        >
                                            {[...Array(totalPages)].map((_, index) => (
                                                <option key={index + 1} value={index + 1}>
                                                    {index + 1}
                                                </option>
                                            ))}
                                        </select>
                                        <p className="dropdown-label-mobile">Go To Page</p>
                                    </div>
                                </div>

                                <div className="pagination-bar">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </div>
                        )}


                    </div>
                </div>
            </MDBCardBody>
            <Toast show={showToast}
                onClose={() => setShowToast(false)}
                position="top-right"
                style={{ position: 'absolute', top: 0, right: 0, zIndex: 2000, backgroundColor: toastType }} delay={3000} autohide>
                <Toast.Body className='text-white'>{toastMessage}</Toast.Body>
            </Toast>
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete {influencerToDelete?.creatorName}?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowDeleteModal(false);
                        setInfluencerToDelete(null);
                    }}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
            <AddToShortlist selectedInfluencers={selectedShortlist} openModal={showAddShortlistModal} closeModal={() => handleShorlistModalClose()} sendToastMessage={updateNotification} />
        </div>
    );
}
